import { useEffect, useState } from "react";
import { Loader2, Sparkles, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";

const toLines = (text) => text.split("\n").map((l) => l.replace(/^[\s•\-*]+/, "").trim()).filter(Boolean);

export const ChangeSummaryDialog = ({ open, sopId, form, nextVersion, saving, onCancel, onConfirm }) => {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(null);

  const generate = async () => {
    setLoading(true);
    setInfo(null);
    try {
      const { data } = await api.post(`/sops/${sopId}/change-summary`, form);
      setText(data.bullets.map((b) => `• ${b}`).join("\n"));
      setInfo(data);
    } catch (e) {
      setInfo({ error: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onCancel()}>
      <DialogContent className="max-w-xl" data-testid="change-summary-dialog">
        <DialogHeader><DialogTitle className="font-display">What changed in v{nextVersion}?</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Staff who follow this SOP get this summary in their notification. Edit freely — one bullet per line.</p>
        {info?.counts && (
          <p className="text-xs text-zinc-500" data-testid="change-summary-counts">
            Steps: {info.counts.added} added · {info.counts.removed} removed · {info.counts.modified} modified
          </p>
        )}
        {loading ? (
          <div className="flex items-center gap-3 rounded-xl border border-lasso-line bg-lasso-tint p-5 text-sm text-lasso-navy" data-testid="change-summary-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> <Sparkles className="h-4 w-4" /> AI is comparing v{nextVersion - 1} with your edits…
          </div>
        ) : (
          <>
            {info?.error && <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="change-summary-error">{info.error}</p>}
            <Textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} placeholder="• Added a step to photograph damage before repairs" data-testid="change-summary-textarea" />
          </>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
          <button onClick={generate} disabled={loading || saving} className="inline-flex items-center gap-2 text-sm font-semibold text-lasso-deep hover:text-lasso-ink disabled:opacity-50" data-testid="change-summary-regenerate">
            <RefreshCw className="h-4 w-4" /> Regenerate
          </button>
          <div className="flex gap-2">
            <button onClick={onCancel} disabled={saving} className="rounded-full border border-lasso-line px-4 py-2.5 text-sm font-semibold hover:border-lasso-ink" data-testid="change-summary-cancel">Keep editing</button>
            <button onClick={() => onConfirm(toLines(text))} disabled={saving || loading} className="inline-flex items-center gap-2 rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy disabled:opacity-60" data-testid="change-summary-confirm">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save as v{nextVersion}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
